import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RiCloseFill } from 'react-icons/ri';
import { HiOutlineSearch } from "react-icons/hi";
import BaseIconInput from "../../BaseIconInput";
import MedicationAccordion from "../../MedicationAccordion";
import MedicationFilterMobileNavbar from "../../MedicationFilterMobileNavbar";
import MedicationSortMobileNavbar from "../../MedicationSortMobileNavbar";
import MedicationFilterDropdown from "../../MedicationFilterDropdown";
import MedicationSortDropdown from "../../MedicationSortDropdown";
import { closeModal, setMedicationFilterOptions } from "../../../store";

const MedicationDetails = ({ medications }) => {
  const sm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const xs = useMediaQuery((theme) => theme.breakpoints.down('ssm'));
  const dispatch = useDispatch();
  const [activeAccordion, setActiveAccordion] = useState(null);

  useMemo(() => {
    if (xs) {
      dispatch(closeModal());
    }
  }, [xs, dispatch]);

  const filterOptions = useSelector(state => state.medicationFilterOptions);

  const [searchKey, setSearchKey] = useState('');
  const [filters, setFilters] = useState([]);
  const [openFilterNav, setOpenFilterNav] = useState(false);
  const [openSortNav, setOpenSortNav] = useState(false);
  const [filteredMedications, setFilteredMedications] = useState([]);
  const [showSearchInput, setShowSearchInput] = useState(true);

  useMemo(() => {
    let medicationsTemp = [...medications];

    if (filterOptions?.riskFactorLevels && filterOptions?.riskFactorLevels.length > 0) {
      const filtersTemp = [];
      medicationsTemp.forEach(item => {
        filterOptions?.riskFactorLevels.forEach(riskFactorLevel => {
          if (item.guidanceLevel.toLowerCase() === riskFactorLevel.toLowerCase()) {
            filtersTemp.push(item);
          }
        });
      });
      medicationsTemp = [...filtersTemp];
    }
    if (filterOptions?.searchKey) {
      medicationsTemp = medicationsTemp.filter(item => item.title.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.brandNames.toString().toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.category.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.class.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.description.toString().toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.evidenceLevel.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.descriptionTitle.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.evidenceLevel.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.genes.toString().toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.guidanceLevel.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()));
    }
    if (filterOptions?.category && filterOptions?.category.length > 0) {
      const filtersTemp = [];
      medicationsTemp.forEach(item => {
        filterOptions?.category?.forEach(option => {
          if (item.category.toLowerCase() === option.toLowerCase()) {
            filtersTemp.push(item);
          }
        });
      });
      medicationsTemp = [...filtersTemp];
    }
    if (filterOptions?.class && filterOptions?.class.length > 0) {
      const filtersTemp = [];
      medicationsTemp.forEach(item => {
        filterOptions?.class.forEach(option => {
          if (item.class.toLowerCase() === option.toLowerCase()) {
            filtersTemp.push(item);
          }
        });
      });
      medicationsTemp = [...filtersTemp];
    }
    if (filterOptions?.gene && filterOptions?.gene.length > 0) {
      const filtersTemp = [];
      medicationsTemp.forEach(item => {
        item.genes.forEach(gene => {
          if (filterOptions?.gene.find(option => option.toLowerCase() === gene.toLowerCase())) {
            filtersTemp.push(item);
          }
        });
      });
      medicationsTemp = [...filtersTemp];
    }
    if (filterOptions?.evidenceLevel) {

    }
    if (filterOptions?.guidanceLevelSortDir) {
      const sortDir = filterOptions.guidanceLevelSortDir;
      medicationsTemp = sortDir === 'asc' ? medicationsTemp.sort((a, b) => a.guidanceLevel.localeCompare(b.guidanceLevel)) : medicationsTemp.sort((a, b) => b.guidanceLevel.localeCompare(a.guidanceLevel));
    }
    if (filterOptions?.alphabetSortDir) {
      const sortDir = filterOptions.alphabetSortDir;
      medicationsTemp = sortDir === 'asc' ? medicationsTemp.sort((a, b) => a.title.localeCompare(b.title)) : medicationsTemp.sort((a, b) => b.title.localeCompare(a.title));
    }
    setFilteredMedications(medicationsTemp);
  }, [filterOptions, medications]);

  useMemo(() => {
    if (filterOptions) {
      const temp = [];
      if (filterOptions?.riskFactorLevels && filterOptions?.riskFactorLevels.length > 0) temp.push({
        type: 'risk factor',
        label: `Risk Factor: ${filterOptions?.riskFactorLevels?.toString()}`,
      });
      if (filterOptions?.searchKey) temp.push({
        type: 'search',
        label: `Filter pill #${filterOptions.searchKey}`,
        value: filterOptions.searchKey
      });
      if (filterOptions?.category) filterOptions?.category.map((val => temp.push({
        type: 'category',
        label: `Filter pill #${val}`,
        value: val
      })));
      if (filterOptions?.class) filterOptions?.class.map((val => temp.push({
        type: 'class',
        label: `Filter pill #${val}`,
        value: val
      })));
      if (filterOptions?.gene) filterOptions?.gene.map((val => temp.push({
        type: 'gene',
        label: `Filter pill #${val}`,
        value: val
      })));
      setFilters(temp);
    }
  }, [filterOptions]);

  useEffect(() => {
    if(searchKey !== '') {
      handleFilter();
    }
  }, []);

  const filterLength = useMemo(() => {
    if (xs) {
      return filters.length - 1;
    } else if (sm) {
      return filters.length - 2;
    }
  }, [sm, xs, filters]);

  const handleFilter = () => {
    const options = {
      ...filterOptions,
      searchKey: searchKey
    };
    dispatch(setMedicationFilterOptions(options));
  };

  const handleClickSearch = () => {
    if (filterOptions.searchKey) {
      handleRemoveFilter(filterOptions?.searchKey);
    }
    handleFilter(searchKey);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      // 👇 Get input value
      handleClickSearch();
    }
  };

  const removeArrayItem = (array, item) => {
    const temp = [...array];
    const index = temp.indexOf(item);
    if (index > -1) { // only splice array when item is found
      temp.splice(index, 1); // 2nd parameter means remove one item only
    }
    return temp;
  };

  const handleRemoveFilter = (filter) => {
    let options = filterOptions;
    if (filter.type === 'risk factor') {
      options = {
        ...options,
        riskFactorLevels: []
      };
    } else if (filter.type === 'search') {
      setSearchKey('');
      options = {
        ...options,
        searchKey: ''
      };
    } else if (filter.type === 'category') {
      const categories = options.category;
      options = {
        ...options,
        category: removeArrayItem(categories, filter.value)
      };
    } else if (filter.type === 'class') {
      const classFilters = options.class;
      options = {
        ...options,
        class: removeArrayItem(classFilters, filter.value)
      };
    } else if (filter.type === 'gene') {
      const gene = options.gene;
      options = {
        ...options,
        gene: removeArrayItem(gene, filter.value)
      };
    }
    dispatch(setMedicationFilterOptions(options));
  };

  const handleClickSort = () => {
    dispatch(setMedicationFilterOptions({
      ...filterOptions,
      guidanceLevelSortDir: filterOptions.guidanceLevelSortDir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAccordionClick = (index) => {
    setActiveAccordion(index);
  };

  return (
    <Box
      sx={{
        padding: '32px 20px 48px',
        bgcolor: 'white',
        boxShadow: '0px 2px 10px 0px #5B87EF33'
      }}
    >
      {openFilterNav && (
        <MedicationFilterMobileNavbar
          isOpen={openFilterNav}
          setIsOpen={setOpenFilterNav}
        />
      )}
      {openSortNav && (
        <MedicationSortMobileNavbar
          isOpen={openSortNav}
          setIsOpen={setOpenSortNav}
        />
      )}
      <Box
        sx={{
          padding: '8px',
          borderRadius: '8px',
          bgcolor: 'primary.white'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { sm: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { sm: 'flex-start', md: 'center' },
              width: '100%'
            }}
          >
            {!showSearchInput ? (
              <Box
                sx={{
                  ml: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowSearchInput(true)}
              >
                <HiOutlineSearch
                  size={20}
                  color='#1B1C1E' 
                />
              </Box>
            ) : (
              <BaseIconInput
                height='48'
                placeholder='Search'
                inputStyles={{
                  background: '#4266B833',
                  borderRadius: '40px',
                  color: '#46545F',
                  border: '#4266b800'
                }}
                sx={{
                  maxWidth: '400px'
                }}
                startIcon={<HiOutlineSearch size={20} />}
                endIcon={<RiCloseFill size={24} />}
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                onClickStartIcon={handleClickSearch}
                onClickEndIcon={() => {
                  let count = 0;
                  while(count < filters.length) {
                    if(filters[count]) {
                      handleRemoveFilter(filters[count]);
                    }
                    count ++;
                  }
                  setSearchKey('');
                }}
                onKeyDown={handleKeyDown}
              />
            )}
            <Box
              sx={{
                display: { xs: showSearchInput ? 'none' : 'flex', md: 'flex' },
              }}
            >
              <MedicationSortDropdown setOpenSortNav={setOpenSortNav} />
              <MedicationFilterDropdown setOpenFilterNav={setOpenFilterNav} />
            </Box>
          </Box>
        </Box>
        <Box>
          <Stack direction={'row'} flexWrap={'wrap'}>
            {filters.map(((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: { xs: idx === 0 ? 'flex' : 'none', ssm: idx > 1 ? 'none' : 'flex', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '2px 6px 2px 6px',
                  borderRadius: '8px',
                  bgcolor: 'primary.lightBlue',
                  mr: '8px',
                  mt: '8px'
                }}
              >
                <Typography variant="body"
                  sx={{
                    mr: '8px',
                    width: { xs: 'auto', ssm: '100px', md: 'auto' },
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}>
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleRemoveFilter(item)}
                >
                  <RiCloseFill size={20} />
                </Box>
              </Box>
            )))}
            {filterLength > 0 && (
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '2px 6px 2px 6px',
                  borderRadius: '8px',
                  bgcolor: 'primary.lightBlue'
                }}
              >
                <Typography variant="body" mr={'8px'}>
                  {`+${filterLength}`}
                </Typography>
              </Box>
            )}
          </Stack>
          <Box
            sx={{
              backgroundColor: 'primary.black',
              boxShadow: '0px 2px 10px 0px #5B87EF33',
              height: '64px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '4px',
              padding: '16px',
              mt: '8px',
              mb: "16px"
            }}
          >
            <Typography variant="h3" color="white">
              Drug name
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={handleClickSort}
            >
              <Typography variant="body" color="white">
                Risk factor
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  ml: '4px'
                }}
              >
                <ExpandLessIcon
                  sx={{
                    color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'asc' ? 'white' : 'primary.mainBlue' : 'white',
                    fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'asc' ? '14px' : '15px'
                  }}
                />
                <ExpandMoreIcon
                  sx={{
                    color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'desc' ? 'white' : 'primary.mainBlue' : 'white',
                    fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'desc' ? '14px' : '15px'
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Grid container spacing={'8px'} mt='1px'>
            {filteredMedications && filteredMedications.map(((item, idx) => (
              <Grid item xs={12} key={`${item.title}-${idx}`}>
                <MedicationAccordion 
                  item={item} 
                  isTalkToUse 
                  zIndex={activeAccordion === idx ? 2 : 1}
                  onClick={() => handleAccordionClick(idx)}
                />
              </Grid>
            )))}
          </Grid>
        </Box>
      </Box>
      {(!filteredMedications || filteredMedications.length === 0) && (
        <Box sx={{
          mt: '16px',
          textAlign: 'center'
        }}>
          <Typography variant="body">No Medications</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MedicationDetails;